import React, { useRef } from "react";
import { Campaign } from "src/campaigns/types";
import XCard from "src/profile/components/XCard";
import { Media } from "src/media/types";
import {
    HorizontalScrollInner,
    HorizontalScrollOuter,
    StatusCard
} from "src/campaigns/details/Details.styled";
import { MediaLoading } from "src/components/SkeletonLoader";
import StoryMediaRow from "src/campaigns/medias/components/StoryMediaRow";
import { AddMediaCell } from "../components/AddCell";
import { useSelector } from "react-redux";
import { isScriptModalOpen } from "src/campaigns/scripts/selectors";
import styled from "styled-components";
import CarouselHeader from "src/campaigns/scripts/components/CarouselHeader";
import ScrollArrows from "src/components/ScrollArrows";
import ProcessingMedias from "src/campaigns/scripts/components/ProcessingMedias";
import { ProcessingControl } from "src/campaigns/scripts/hooks/useProcessing";

interface StyleProps {
    $isScriptModal: boolean;
    $isAdmin: boolean;
}
const StyledXCard = styled(XCard)<StyleProps>`
    height: ${props =>
        props.$isScriptModal && props.$isAdmin ? "100%" : "initial"};
    min-height: ${props =>
        props.$isScriptModal && props.$isAdmin ? "232px" : "initial"};
    & > * {
        height: ${props =>
            props.$isScriptModal && props.$isAdmin ? "100%" : "170px"};
    }
`;
const StyledStatusCard = styled(StatusCard)<StyleProps>`
    margin: ${props =>
        props.$isScriptModal && props.$isAdmin ? "5px" : "8px"};
`;
interface Props {
    name: string;
    currentId: number | null;
    campaign?: Campaign;
    fetchMedias: (id: number) => void;
    medias?: Media[];
    mediasLoading: boolean;
    isAdmin?: boolean;
    processingControl?: ProcessingControl;
    isScriptModal?: boolean;
}

const MediasAccordion = (props: Props) => {
    const {
        name,
        currentId,
        campaign,
        fetchMedias,
        medias,
        mediasLoading,
        isAdmin = false,
        processingControl,
        isScriptModal = false
    } = props;

    const carouselRef = useRef(null);

    if (mediasLoading)
        return (
            <HorizontalScrollOuter overflow="hidden">
                <HorizontalScrollInner>
                    <MediaLoading />
                    <MediaLoading />
                    <MediaLoading />
                    <MediaLoading />
                    <MediaLoading />
                    <MediaLoading />
                    <MediaLoading />
                    <MediaLoading />
                </HorizontalScrollInner>
            </HorizontalScrollOuter>
        );

    if (!medias)
        return (
            <HorizontalScrollOuter overflow="hidden" bottomBorder="0px">
                <HorizontalScrollInner>
                    <StyledStatusCard
                        $isAdmin={isAdmin}
                        $isScriptModal={isScriptModal}
                    >
                        <StyledXCard
                            $isAdmin={isAdmin}
                            $isScriptModal={isScriptModal}
                            text="Failed to load media resources, click the button below to try again."
                            button={{
                                text: "Try again",
                                callback: () => {
                                    currentId && fetchMedias(currentId);
                                }
                            }}
                        />
                    </StyledStatusCard>
                </HorizontalScrollInner>
            </HorizontalScrollOuter>
        );

    if (medias.length && campaign)
        return (
            <>
                <CarouselHeader
                    rightComponent={<ScrollArrows refElement={carouselRef} />}
                    leftComponent={
                        isAdmin && processingControl && currentId ? (
                            <ProcessingMedias
                                currentCount={medias.length}
                                processingControl={processingControl}
                                handleRefresh={() => fetchMedias(currentId)}
                            />
                        ) : null
                    }
                />

                <StoryMediaRow
                    Media={medias}
                    campaign={campaign}
                    linkPrefix={campaign.linkUrlPrefix}
                    globalModal
                    isScriptModal={isScriptModal}
                    isAdmin={isAdmin}
                    carouselRef={carouselRef}
                />
            </>
        );

    return (
        <>
            {isAdmin && processingControl && currentId && (
                <CarouselHeader
                    leftComponent={
                        <ProcessingMedias
                            currentCount={medias.length}
                            processingControl={processingControl}
                            handleRefresh={() => fetchMedias(currentId)}
                        />
                    }
                />
            )}
            <HorizontalScrollOuter
                overflow="hidden"
                bottomBorder="0px"
                topBorder={isScriptModal && isAdmin ? "0px" : undefined}
            >
                <HorizontalScrollInner>
                    {isScriptModal && isAdmin && (
                        <AddMediaCell caption="Add Example Media to Script" />
                    )}
                    <StyledStatusCard
                        $isAdmin={isAdmin}
                        $isScriptModal={isScriptModal}
                    >
                        <StyledXCard
                            $isAdmin={isAdmin}
                            $isScriptModal={isScriptModal}
                            text={`This ${name} doesn’t have any media resources yet`}
                            button={{
                                text: "Try again",
                                callback: () => {
                                    currentId && fetchMedias(currentId);
                                }
                            }}
                        />
                    </StyledStatusCard>
                </HorizontalScrollInner>
            </HorizontalScrollOuter>
        </>
    );
};

export default MediasAccordion;
